import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaCalendarCheck, FaTicketAlt, FaUsers, FaStar, FaChartLine, FaTrophy, FaClock } from 'react-icons/fa';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import GlobalHeader from '../../../../components/GlobalHeader.jsx';
import { logout } from '../../../../utils/api/auth';
import { fetchOrganiserEventPerformanceAnalytics } from '../../../../utils/api/organiserApi.js';
import '../css/EventPerformanceDashboard.css';
import OverviewCards from '../../../../components/OverviewCards.jsx';
import CollapsibleTable from '../../../../components/CollapsibleTable.jsx';
import '../../AdminDash/css/AdminDash.css';
import '../../../../components/css/AdminAnalytics.css';

function EventPerformanceDashboard() {
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [analyticsData, setAnalyticsData] = useState({
    totalEvents: 0,
    activeEvents: 0,
    completedEvents: 0,
    successRate: 0,
    averageAttendance: 0,
    topPerformingEvents: [],
    eventCategories: [],
    monthlyPerformance: [],
    attendanceTrends: []
  });

  useEffect(() => {
    fetchEventPerformanceData();
  }, []);

  const fetchEventPerformanceData = async () => {
    try {
      setLoading(true);
      const data = await fetchOrganiserEventPerformanceAnalytics();

      const totalEvents = data?.totalEvents ?? 0;
      const completedEvents = data?.completedEvents ?? 0;
      const derivedSuccessRate = totalEvents ? Number(((completedEvents / totalEvents) * 100).toFixed(1)) : 0;

      const sortedTopEvents = Array.isArray(data?.topPerformingEvents)
        ? [...data.topPerformingEvents].sort((a, b) => Number(b?.revenue ?? 0) - Number(a?.revenue ?? 0))
        : [];

      setAnalyticsData({
        totalEvents,
        activeEvents: data?.activeEvents ?? 0,
        completedEvents,
        successRate: data?.successRate ?? derivedSuccessRate,
        averageAttendance: data?.averageAttendance ?? 0,
        topPerformingEvents: sortedTopEvents,
        eventCategories: Array.isArray(data?.eventCategories) ? data.eventCategories : [],
        monthlyPerformance: Array.isArray(data?.monthlyPerformance) ? data.monthlyPerformance : [],
        attendanceTrends: Array.isArray(data?.attendanceTrends) ? data.attendanceTrends : []
      });
    } catch (error) {
      console.error('Error fetching event performance data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout(nav);
  };

  // Prepare data for OverviewCards component
  const successRate = analyticsData.successRate ?? (analyticsData.totalEvents > 0
    ? Math.round((analyticsData.completedEvents / analyticsData.totalEvents) * 100)
    : 0);

  const overviewCards = [
    {
      icon: <FaCalendarCheck />,
      title: 'Total Events',
      value: analyticsData.totalEvents,
      subtitle: `${analyticsData.activeEvents} active`,
      color: '#0786f5ff'
    },
    {
      icon: <FaUsers />,
      title: 'Avg Attendance',
      value: `${analyticsData.averageAttendance}%`,
      subtitle: 'Capacity utilization',
      color: '#0786f5ff'
    },
    {
      icon: <FaTicketAlt />,
      title: 'Completed Events',
      value: analyticsData.completedEvents,
      subtitle: 'Successfully hosted',
      color: '#0786f5ff'
    }
  ];

  const formatCurrency = (amount) => {
    const value = Number(amount ?? 0);
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR',
      minimumFractionDigits: 0,
    }).format(value);
  };

  // Prepare data for CollapsibleTable component
  const topEventsColumns = [
    { header: 'Event Name', key: 'name' },
    { 
      header: 'Category', 
      key: 'category',
      render: (value) => value || 'Unknown'
    },
    { header: 'Attendance', key: 'attendance', render: (value) => Number(value ?? 0).toLocaleString() },
    { header: 'Capacity', key: 'capacity', render: (value) => Number(value ?? 0).toLocaleString() },
    { 
      header: 'Fill Rate', 
      key: 'attendance',
      render: (value, row) => {
        const attendance = Number(value ?? 0);
        const capacity = Number(row.capacity ?? 0) || 1;
        return `${Math.round((attendance / capacity) * 100)}%`;
      }
    }
  ];

  const categoryLeaderInsight = (() => {
    const categories = analyticsData.eventCategories;
    if (!categories.length) return null;
    const leader = categories.reduce((prev, current) => (current.avgAttendance > prev.avgAttendance ? current : prev));
    return {
      type: 'positive',
      icon: <FaTrophy />,
      title: `${leader.category} leading attendance`,
      description: `${leader.category} averages ${leader.avgAttendance}% attendance across ${leader.count} events.`
    };
  })();

  const momentumInsight = (() => {
    const perf = analyticsData.monthlyPerformance;
    if (!perf.length) return null;
    const latest = perf[perf.length - 1];
    const previous = perf[perf.length - 2];
    if (!previous) return null;
    const delta = latest.events - previous.events;
    if (!delta) return null;
    const trendUp = delta > 0;
    return {
      type: trendUp ? 'positive' : 'neutral',
      icon: <FaChartLine />,
      title: trendUp ? 'Events trending upward' : 'Events stabilising',
      description: `${latest.month} recorded ${Math.abs(delta)} ${delta > 0 ? 'more' : 'fewer'} events than ${previous.month}.`
    };
  })();

  const successInsight = (() => {
    if (!analyticsData.totalEvents) return null;
    return {
      type: successRate >= 75 ? 'positive' : 'neutral',
      icon: <FaClock />,
      title: 'Overall success rate',
      description: `${successRate}% of ${analyticsData.totalEvents} events completed successfully.`
    };
  })();

  const insights = [categoryLeaderInsight, momentumInsight, successInsight].filter(Boolean);

  if (loading) {
    return (
      <div className="event-performance-dashboard">
        <GlobalHeader
          title="Event Performance Dashboard"
          subtitle="Loading event data..."
          onLogout={handleLogout}
          backTo="/organiser-dash"
        />
        <div className="loading-container">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="event-performance-dashboard">
      <GlobalHeader
        title="Event Performance Dashboard"
        subtitle="Analyze event success rates, attendance, and popularity"
        onLogout={handleLogout}
        backTo="/organiser-dash"
      />

      <main className="event-performance-dashboard__main">
        {/* Overview Cards */}
        <OverviewCards cards={overviewCards} />

        {/* Top Performing Events */}
        <CollapsibleTable 
          title="Top Performing Events"
          data={analyticsData.topPerformingEvents}
          columns={topEventsColumns}
          formatCurrency={formatCurrency}
          defaultCollapsed={true}
        />

        {/* Charts Section */}
        <section className="analytics-charts">
          <div className="chart-container">
            <h3>Monthly Performance Trends</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={analyticsData.monthlyPerformance}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis yAxisId="left" />
                <YAxis yAxisId="right" orientation="right" />
                <Tooltip />
                <Legend />
                <Line yAxisId="left" type="monotone" dataKey="avgAttendance" stroke="#EFB034FF" strokeWidth={2} name="Avg Attendance %" />
                <Line yAxisId="right" type="monotone" dataKey="events" stroke="#c3e1f8" strokeWidth={2} name="Number of Events" />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="chart-container">
            <h3>Event Categories Performance</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={analyticsData.eventCategories}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="category" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="avgAttendance" fill="#EFB034FF" name="Avg Attendance %" />
                <Bar dataKey="count" fill="#b8f5cd" name="Number of Events" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </section>
      </main>
    </div>
  );
}

export default EventPerformanceDashboard;
